import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { User } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../hooks/useIsMobile";
import { useNoIndex } from "../hooks/useNoIndex";
import { lookupLeagueByCodePublic, joinLeague, LeagueLookupResult } from "../api/leagues";
import { ApiError } from "../api/client";
import { captureJoinLanding } from "../lib/attribution";
import AuthModal, { AuthModalMode } from "../components/AuthModal";
import { MODE_CONTENT } from "./createLeague/modeContent";
import { card, widePageContainer, primaryBtn, secondaryBtn, errorStyle } from "../styles";

interface Props {
  user: User | null;
}

/**
 * The public landing page for a shared invite link (https://scoracle.cc/join/<code>) — unlike
 * JoinLeaguePage (/leagues/join, authenticated, manual code entry), this works for a
 * logged-out visitor: it previews the league via the now-public lookup endpoint, and if the
 * visitor isn't signed in, opens AuthModal and auto-completes the join once they are, rather
 * than making them re-type a code they already clicked a link to get to.
 */
export default function PublicJoinPage({ user }: Props) {
  const { code = "" } = useParams<{ code: string }>();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  useNoIndex(); // private league preview, thin duplicate content — never indexed

  const [preview, setPreview] = useState<LeagueLookupResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [lookupError, setLookupError] = useState("");
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState("");
  const [authModalMode, setAuthModalMode] = useState<AuthModalMode | null>(null);
  const [pendingAutoJoin, setPendingAutoJoin] = useState(false);

  useEffect(() => {
    if (code) captureJoinLanding(code);
  }, [code]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLookupError("");
    lookupLeagueByCodePublic(code, user)
      .then((data) => { if (active) setPreview(data); })
      .catch((err) => {
        if (!active) return;
        setLookupError(err instanceof ApiError && err.status === 404 ? t("joinLeague.errors.notFound") : t("joinLeague.errors.lookupFailed"));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // Re-run on auth state change too — alreadyMember depends on who's asking.
  }, [code, user, t]);

  useEffect(() => {
    if (user && pendingAutoJoin) {
      setPendingAutoJoin(false);
      void handleJoin();
    }
    // handleJoin is stable enough for this purpose; only react to `user` actually changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleJoin = async () => {
    if (!user) {
      setPendingAutoJoin(true);
      setAuthModalMode("register");
      return;
    }
    setJoinError("");
    setJoining(true);
    try {
      const data = await joinLeague(user, code.toUpperCase());
      navigate(`/leagues/${data.id}`, { state: { justJoined: true } });
    } catch (err) {
      setJoinError(err instanceof ApiError ? err.message : t("joinLeague.errors.failed"));
    } finally {
      setJoining(false);
    }
  };

  const Icon = preview ? MODE_CONTENT[preview.mode].icon : null;

  return (
    <div className="sg-fade-in" style={{ ...widePageContainer, padding: "1.5rem 0" }}>
      <section style={{ marginBottom: "2.5rem" }}>
        <div style={{
          borderRadius: 20,
          padding: isMobile ? "2rem 1.5rem" : "3rem 3.5rem",
          background: "var(--color-surface-alt)",
          border: "1px solid var(--color-border-subtle)",
        }}>
          <div style={{
            fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase",
            letterSpacing: "0.12em", color: "var(--color-text-dim)", marginBottom: "0.9rem",
          }}>
            {t("joinLeague.public.eyebrow")}
          </div>
          <h1 style={{
            margin: "0 0 1.1rem",
            fontFamily: "var(--font-display)",
            fontSize: isMobile ? "1.9rem" : "2.6rem",
            fontWeight: 800,
            color: "var(--color-text)",
            lineHeight: 1.15,
          }}>
            {preview ? t("joinLeague.public.heading", { name: preview.name }) : t("joinLeague.public.headingLoading")}
          </h1>
          <p style={{ margin: 0, fontSize: "1rem", color: "var(--color-text-muted)", lineHeight: 1.7, maxWidth: 620 }}>
            {t("joinLeague.public.subtitle")}
          </p>
        </div>
      </section>

      <div style={{ maxWidth: 480 }}>
        {loading && (
          <div style={card}>
            <p style={{ margin: 0, color: "var(--color-text-muted)" }}>{t("common.loading")}</p>
          </div>
        )}

        {!loading && lookupError && (
          <div style={card}>
            <p style={errorStyle}>{lookupError}</p>
            <Link to="/" style={{ ...secondaryBtn, textDecoration: "none", display: "inline-block" }}>
              {t("joinLeague.public.backHome")}
            </Link>
          </div>
        )}

        {!loading && preview && (
          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.25rem" }}>
              {Icon && (
                <div style={{
                  width: 40, height: 40, borderRadius: 8, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "var(--color-surface-alt)", color: "var(--color-accent)",
                }}>
                  <Icon />
                </div>
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "var(--color-text)" }}>
                  {preview.name}
                </div>
                <div style={{ fontSize: "0.78rem", color: "var(--color-text-muted)" }}>
                  {t(`createLeague.mode.${preview.mode}.label`)} · {t("joinLeague.membersLabel", { count: preview.memberCount })} · {preview.competition.name} {preview.competition.season}
                </div>
              </div>
            </div>

            {preview.alreadyMember ? (
              <>
                <p style={{ margin: "0 0 0.75rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  {t("joinLeague.alreadyMember.title")}
                </p>
                <Link to={`/leagues/${preview.id}`} style={{ ...primaryBtn, textDecoration: "none", display: "inline-block" }}>
                  {t("joinLeague.alreadyMember.cta")} →
                </Link>
              </>
            ) : !preview.joinable ? (
              <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                {t("joinLeague.notJoinable")}
              </p>
            ) : (
              <>
                {joinError && <p style={errorStyle}>{joinError}</p>}
                <button onClick={handleJoin} disabled={joining} style={{ ...primaryBtn, width: "100%" }}>
                  {joining
                    ? t("common.saving")
                    : user
                      ? `${t("joinLeague.joinCta", { name: preview.name })} →`
                      : `${t("joinLeague.public.signInToJoin")} →`}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {authModalMode && (
        <AuthModal
          initialMode={authModalMode}
          onClose={() => { setAuthModalMode(null); setPendingAutoJoin(false); }}
          onAuthenticated={() => setAuthModalMode(null)}
        />
      )}
    </div>
  );
}
